import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard,
    Sparkles,
    CreditCard,
    Wallet,
    UserCircle2,
    Users2,
    Building2,
    Inbox,
    Users,
    ShieldCheck,
    Package,
    Store,
    Database,
    Receipt,
    Landmark,
    Ticket,
    Briefcase,
    Gift,
    UserCheck,
    Globe,
    ShieldAlert,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import CountryLanguageSelector from "@/components/CountryLanguageSelector";

// Nav definitions keyed by i18n token — the label is rendered via t() at
// render time so switching language updates the sidebar instantly.
const NAV_BY_ROLE = {
    member: [
        { to: "/member", key: "member_overview", icon: LayoutDashboard, end: true },
        { to: "/member/deals", key: "member_deals", icon: Sparkles },
        { to: "/member/subscriptions", key: "member_subs", icon: CreditCard },
        { to: "/member/payments", key: "member_payments", icon: Wallet },
        { to: "/member/family", key: "member_family", icon: Users2 },
        { to: "/member/profile", key: "member_profile", icon: UserCircle2 },
    ],
    broker: [
        { to: "/broker", key: "broker_dashboard", icon: LayoutDashboard, end: true },
        { to: "/broker/profiles", key: "broker_profiles", icon: Store },
        { to: "/broker/products", key: "broker_products", icon: Package },
        { to: "/broker/purchases", key: "broker_purchases", icon: Receipt },
        { to: "/broker/applications", key: "broker_apps", icon: Inbox },
        { to: "/broker/payout-details", key: "broker_payout", icon: Landmark },
    ],
    affiliate: [
        { to: "/affiliate", key: "affiliate_dashboard", icon: Gift, end: true },
        { to: "/affiliate/payments", key: "affiliate_payments", icon: Wallet },
        { to: "/affiliate/payout-details", key: "affiliate_payout", icon: Landmark },
        { to: "/affiliate/profile", key: "affiliate_profile", icon: UserCircle2 },
    ],
    admin: [
        { to: "/admin", key: "admin_dashboard", icon: LayoutDashboard, end: true },
        { to: "/admin/users", key: "admin_users", icon: Users },
        { to: "/admin/memberships", key: "admin_memberships", icon: ShieldCheck },
        { to: "/admin/vendors", key: "admin_vendors", icon: Building2 },
        { to: "/admin/products", key: "admin_products", icon: Package },
        { to: "/admin/countries", key: "admin_countries", icon: Globe },
        { to: "/admin/applications", key: "admin_apps", icon: Inbox },
        { to: "/admin/affiliate-applications", key: "admin_aff_apps", icon: UserCheck },
        { to: "/admin/affiliate-payouts", key: "admin_aff_payouts", icon: Wallet },
        { to: "/admin/transactions", key: "admin_txns", icon: Receipt },
        { to: "/admin/promo-codes", key: "admin_promos", icon: Ticket },
        { to: "/admin/stripe-products", key: "admin_stripe_products", icon: CreditCard },
        { to: "/admin/stripe-discounts", key: "admin_stripe_discounts", icon: Ticket },
        { to: "/admin/suspicious-checkouts", key: "admin_suspicious", icon: ShieldAlert },
        { to: "/admin/data", key: "admin_data", icon: Database },
    ],
};

const ROLE_LABEL = {
    member: "portal.member",
    broker: "portal.broker",
    affiliate: "portal.affiliate",
    admin: "portal.admin",
};

export default function DashboardLayout() {
    const { user, logout, switchRole } = useAuth();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const role = user?.active_role || user?.role || "member";
    const nav = NAV_BY_ROLE[role] || NAV_BY_ROLE.member;
    const heldRoles = user?.roles || (user?.role ? [user.role] : []);
    const switchableRoles = heldRoles.filter((r) => r !== "admin");
    const [switching, setSwitching] = useState(null);

    const handleSwitch = async (target) => {
        if (target === role || switching) return;
        setSwitching(target);
        try {
            await switchRole(target);
            const home =
                target === "broker"
                    ? "/broker"
                    : target === "affiliate"
                    ? "/affiliate"
                    : "/member";
            navigate(home);
        } finally {
            setSwitching(null);
        }
    };

    return (
        <div className="min-h-screen bg-cream flex" data-testid={`layout-${role}`}>
            {/* Sidebar */}
            <aside
                className={`fixed lg:sticky lg:top-0 top-0 left-0 z-40 h-screen w-72 bg-navy-700 text-navy-50 border-r border-navy-800 flex flex-col transition-transform ${
                    open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                }`}
            >
                <div className="px-7 py-8 border-b border-white/10">
                    <Link to="/" className="flex items-center gap-3">
                        <img
                            src="/perx_logo_golden.png"
                            alt="Perx"
                            className="h-9 w-9 object-contain"
                        />
                        <div className="leading-none">
                            <div className="font-display text-xl text-white">
                                Perx Group
                            </div>
                            <div className="text-[10px] tracking-[0.3em] text-gold-500 uppercase mt-1">
                                {t(ROLE_LABEL[role])}
                            </div>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                    {switchableRoles.length > 1 && (
                        <div
                            data-testid="portal-switcher"
                            className="mb-4 rounded-xl bg-white/5 border border-white/10 p-1.5"
                        >
                            <div className="text-[9px] uppercase tracking-widest text-gold-400 px-2 pt-1 pb-1.5">
                                {t("portal.switch_portal")}
                            </div>
                            <div className="grid gap-1">
                                {switchableRoles.map((r) => (
                                    <button
                                        key={r}
                                        type="button"
                                        disabled={switching === r}
                                        onClick={() => handleSwitch(r)}
                                        data-testid={`portal-switch-${r}`}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-widest transition ${
                                            role === r
                                                ? "bg-gold-500 text-navy-800"
                                                : "text-navy-50/70 hover:bg-white/10 hover:text-white"
                                        }`}
                                    >
                                        {t(ROLE_LABEL[r] || r)}
                                        {role === r && (
                                            <span className="ml-1 text-[9px] opacity-70">
                                                · Active
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {nav.map((n) => {
                        const Icon = n.icon;
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={`sidebar-${n.key.replace(/_/g, "-")}`}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `group flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                        isActive
                                            ? "bg-white text-navy-700"
                                            : "text-navy-50/80 hover:bg-white/5 hover:text-white"
                                    }`
                                }
                            >
                                <Icon size={18} strokeWidth={1.7} />
                                <span>{t(`sidebar.${n.key}`)}</span>
                            </NavLink>
                        );
                    })}
                    {role === "member" && user?.membership_type === "business" && (
                        <NavLink
                            to="/member/employees"
                            data-testid="sidebar-my-employees"
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `group flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-white text-navy-700"
                                        : "text-navy-50/80 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Briefcase size={18} strokeWidth={1.7} />
                            <span>{t("sidebar.member_employees")}</span>
                        </NavLink>
                    )}
                </nav>

                <div className="px-4 py-5 border-t border-white/10">
                    <div className="mb-3 flex justify-center">
                        <CountryLanguageSelector compact />
                    </div>
                    <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5">
                        <div className="h-9 w-9 rounded-full bg-gold-500 text-navy-900 flex items-center justify-center font-semibold">
                            {user?.name?.[0] || "P"}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                                {user?.name}
                            </div>
                            <div className="text-[11px] text-navy-50/60 truncate">
                                {user?.email}
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid="sidebar-logout"
                        onClick={logout}
                        className="mt-3 w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-navy-50/70 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        <LogOut size={18} strokeWidth={1.7} />
                        {t("portal.sign_out")}
                    </button>
                </div>
            </aside>

            {/* Mobile overlay */}
            {open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 z-30 bg-navy-900/40 lg:hidden"
                />
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
                <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-gray-100 h-16 px-4 flex items-center justify-between">
                    <button
                        data-testid="mobile-nav-open"
                        onClick={() => setOpen(true)}
                        className="p-2 text-navy-700"
                    >
                        <Menu size={22} />
                    </button>
                    <Link to="/" className="flex items-center gap-2">
                        <img
                            src="/perx_logo.png"
                            alt="Perx"
                            className="h-7 w-7 object-contain"
                        />
                        <span className="font-display text-lg text-navy-700">Perx</span>
                    </Link>
                    <div className="w-10" />
                </header>

                <div key={location.pathname} className="animate-fade-up">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}

export function PageHeader({ eyebrow, title, description, action, testid = "page-header" }) {
    return (
        <div
            data-testid={testid}
            className="border-b border-navy-100/70 bg-white px-6 lg:px-12 py-8 lg:py-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
            <div>
                {eyebrow && (
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                        {eyebrow}
                    </div>
                )}
                <h1 className="font-display text-3xl md:text-4xl text-navy-700 mb-2">
                    {title}
                </h1>
                {description && (
                    <p className="text-navy-500 max-w-xl leading-relaxed">
                        {description}
                    </p>
                )}
            </div>
            {action}
        </div>
    );
}
